import { useState } from "react";
import { Sparkles } from "lucide-react";
import { PillButton, SectionBadge } from "./SitePrimitives";
import { PhoneShot, SolutionIcon, Tag } from "./SiteSolutions";

export type SolutionCard = {
  key: string;
  name: string;
  href: string;
  kind: string;
  category: string;
  summary: string;
  icon?: string;
  image?: string;
  tags: string[];
  available: boolean;
};

export type SolutionsPageLabels = {
  all: string;
  categories: Record<string, string>;
  details: string;
  available: string;
  soon: string;
};

/** /solutions: header, category filters (only with more than one solution), cards and a contact card. */
export function SolutionsIndexPage({
  badge,
  title,
  description,
  solutions,
  labels,
  soonCard,
  ideaCard,
  onCta,
}: {
  badge: string;
  title: string;
  description: string;
  solutions: SolutionCard[];
  labels: SolutionsPageLabels;
  soonCard?: { kind: string; title: string; text: string };
  ideaCard: { kind: string; title: string; text: string; cta: string };
  onCta: (href: string, placement: string) => void;
}) {
  const [filter, setFilter] = useState("all");
  const categories = Array.from(new Set(solutions.map((item) => item.category).filter(Boolean)));
  const showFilters = solutions.length > 1 && categories.length > 1;
  const shown = filter === "all" ? solutions : solutions.filter((item) => item.category === filter);
  return (
    <>
      <section
        id="hero"
        className="bg-[radial-gradient(ellipse_at_50%_0%,rgba(40,241,231,0.14),transparent_60%)] px-6 pb-14 pt-[150px] text-center min-[810px]:pb-[70px] min-[810px]:pt-[180px]"
      >
        <div className="mx-auto flex max-w-[780px] flex-col items-center">
          <SectionBadge>{badge}</SectionBadge>
          <h1 className="site-h1 mt-3 text-white">{title}</h1>
          {description ? <p className="site-lead mt-5 max-w-[600px]">{description}</p> : null}
        </div>
        {showFilters ? (
          <div className="mt-9 flex flex-wrap justify-center gap-2" role="tablist">
            {["all", ...categories].map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={filter === key}
                onClick={() => setFilter(key)}
                className={`rounded-full border px-4 py-2 font-['Inter'] text-[14px] font-semibold transition-colors ${
                  filter === key
                    ? "border-[#1F4A48] bg-[#0F2827] text-[var(--public-accent)]"
                    : "border-[#2B3A3A] text-[var(--public-muted)] hover:text-white"
                }`}
              >
                {key === "all" ? labels.all : labels.categories[key] || key}
              </button>
            ))}
          </div>
        ) : null}
      </section>

      <section className="site-container pb-6">
        <div className="grid gap-6 min-[810px]:grid-cols-2 min-[1200px]:grid-cols-3">
          {shown.map((item) => (
            <a
              key={item.key}
              href={item.href}
              className="group flex flex-col overflow-hidden rounded-[20px] border border-[#1C2626] bg-[#111818] transition-colors hover:border-[#1F4A48]"
            >
              <div className="flex h-[230px] justify-center overflow-hidden bg-[linear-gradient(160deg,#0F2827,#0B1515)] pt-7">
                {item.image ? (
                  <div className="w-[150px] overflow-hidden rounded-t-[22px] border-[5px] border-b-0 border-[#1F2A2A] bg-white">
                    <img src={item.image} alt="" loading="lazy" className="block w-full" />
                  </div>
                ) : (
                  <Sparkles className="mt-14 h-12 w-12 text-[#3F5A59]" aria-hidden="true" />
                )}
              </div>
              <div className="flex flex-1 flex-col gap-[10px] px-6 pb-[26px] pt-[22px]">
                <div className="flex items-center gap-3">
                  {item.icon ? <img src={item.icon} alt="" className="h-10 w-10 rounded-[10px]" /> : null}
                  <div>
                    {item.kind ? (
                      <p className="font-['Inter'] text-[12px] font-bold uppercase tracking-[0.06em] text-[var(--public-accent)]">{item.kind}</p>
                    ) : null}
                    <h2 className="text-[22px] font-bold leading-[1.15] text-white">{item.name}</h2>
                  </div>
                </div>
                {item.summary ? <p className="site-body text-white/70 min-[810px]:text-[14px]">{item.summary}</p> : null}
                <div className="mb-2 mt-1 flex flex-wrap gap-2">
                  <Tag live={item.available}>{item.available ? labels.available : labels.soon}</Tag>
                  {item.tags.map((tag) => (
                    <Tag key={tag}>{tag}</Tag>
                  ))}
                </div>
                <span className="mt-auto font-['Inter'] text-[14px] font-semibold text-[var(--public-accent)] group-hover:underline">
                  {labels.details} →
                </span>
              </div>
            </a>
          ))}
          {soonCard && filter === "all" ? (
            <article className="flex flex-col overflow-hidden rounded-[20px] border border-dashed border-[#2B3A3A]">
              <div className="grid h-[230px] place-items-center">
                <Sparkles className="h-12 w-12 text-[#3F5A59]" aria-hidden="true" />
              </div>
              <div className="flex flex-col gap-[10px] px-6 pb-[26px] pt-[22px] opacity-75">
                <p className="font-['Inter'] text-[12px] font-bold uppercase tracking-[0.06em] text-[var(--public-accent)]">{soonCard.kind}</p>
                <h2 className="text-[22px] font-bold leading-[1.15] text-white">{soonCard.title}</h2>
                <p className="site-body text-white/70 min-[810px]:text-[14px]">{soonCard.text}</p>
              </div>
            </article>
          ) : null}
          <article className="flex flex-col justify-center gap-[10px] rounded-[20px] border border-[#1F4A48] bg-[#0F2827] p-7">
            <p className="font-['Inter'] text-[12px] font-bold uppercase tracking-[0.06em] text-[var(--public-accent)]">{ideaCard.kind}</p>
            <h2 className="text-[26px] font-bold leading-[1.15] text-white">{ideaCard.title}</h2>
            <p className="site-body mb-3 text-white/75 min-[810px]:text-[14px]">{ideaCard.text}</p>
            {ideaCard.cta ? (
              <PillButton href="#contact" size="sm" className="self-start" onClick={() => onCta("#contact", "solutions-idea")}>
                {ideaCard.cta}
              </PillButton>
            ) : null}
          </article>
        </div>
      </section>
    </>
  );
}

export type SolutionDetail = {
  name: string;
  kind: string;
  byLabel: string;
  headline: string;
  description: string;
  icon?: string;
  screenshots: string[];
  platform: string;
  appStoreUrl?: string;
  features: Array<{ title: string; description: string; icon?: string }>;
  dataBadge: string;
  dataTitle: string;
  dataPoints: string[];
  links: Array<{ label: string; href: string; primary?: boolean }>;
};

function AppStoreBadge({ href, locale }: { href: string; locale: "en" | "es" }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-[10px] rounded-xl border border-[#3A3A3A] bg-black px-4 py-2 font-['Inter'] text-white transition-colors hover:border-[var(--public-accent)]"
    >
      <svg viewBox="0 0 384 512" className="h-6 w-6" aria-hidden="true" fill="currentColor">
        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
      </svg>
      <span>
        <small className="block text-[10px] leading-[1.2]">{locale === "es" ? "Descárgala en el" : "Download on the"}</small>
        <b className="block text-[17px] leading-none">App Store</b>
      </span>
    </a>
  );
}

/** /solutions/<slug>: the app's home page (the one registered with Google for OAuth verification). */
export function SolutionDetailPage({ solution, locale }: { solution: SolutionDetail; locale: "en" | "es" }) {
  const shots = solution.screenshots.slice(0, 3);
  return (
    <>
      <section id="hero" className="site-container grid items-center gap-12 pb-16 pt-[140px] min-[810px]:pt-[170px] min-[1200px]:grid-cols-[1.1fr_1fr] min-[1200px]:pb-20">
        <div>
          {solution.icon ? <img src={solution.icon} alt={`${solution.name} icon`} className="h-[84px] w-[84px] rounded-[20px]" /> : null}
          <p className="site-badge mt-6">
            {[solution.kind, solution.byLabel].filter(Boolean).join(" · ")}
          </p>
          <h1 className="site-h1 mt-3 text-white">{solution.headline || solution.name}</h1>
          {solution.description ? <p className="site-lead mb-7 mt-5 max-w-[500px] min-[1200px]:text-[16px]">{solution.description}</p> : null}
          <div className="flex flex-wrap items-center gap-4">
            {solution.appStoreUrl ? <AppStoreBadge href={solution.appStoreUrl} locale={locale} /> : null}
            {solution.platform ? <span className="site-body text-white/70">{solution.platform}</span> : null}
          </div>
        </div>
        {shots.length ? (
          <div className="flex justify-center gap-3 min-[810px]:gap-4">
            {shots.map((src, index) => (
              <PhoneShot
                key={src}
                src={src}
                className={`w-[31%] max-w-[170px] ${index === 1 && shots.length === 3 ? "-translate-y-7" : ""}`}
              />
            ))}
          </div>
        ) : null}
      </section>

      {solution.features.length ? (
        <section className="site-container pb-16 min-[1200px]:pb-[90px]">
          <div className="grid gap-5 min-[810px]:grid-cols-2 min-[1200px]:grid-cols-3">
            {solution.features.map((feature) => (
              <article key={feature.title} className="rounded-[20px] border border-[#1C2626] bg-[#111818] p-7">
                <SolutionIcon name={feature.icon} />
                <h2 className="mt-4 text-[19px] font-bold leading-[1.2] text-white">{feature.title}</h2>
                {feature.description ? <p className="site-body mt-2 text-white/70 min-[810px]:text-[14px]">{feature.description}</p> : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {solution.dataPoints.length || solution.links.length ? (
        <section id="privacy" className="site-container">
          <div className="grid gap-10 rounded-[20px] border border-[#1F4A48] bg-[#0D1616] p-7 min-[810px]:p-10 min-[1200px]:grid-cols-[1fr_1.3fr]">
            <div>
              <SectionBadge>{solution.dataBadge}</SectionBadge>
              <h2 className="site-h2 mt-2 text-white">{solution.dataTitle}</h2>
              <div className="mt-5 flex flex-wrap gap-3">
                {solution.links.map((link) =>
                  link.primary ? (
                    <PillButton key={link.href} href={link.href} size="sm">
                      {link.label}
                    </PillButton>
                  ) : (
                    <a
                      key={link.href}
                      href={link.href}
                      className="inline-flex items-center rounded-xl border border-[#2E3B3B] px-4 py-[10px] text-[16px] font-semibold text-white transition-colors hover:border-[var(--public-accent)]"
                    >
                      {link.label}
                    </a>
                  )
                )}
              </div>
            </div>
            {solution.dataPoints.length ? (
              <ul className="list-disc space-y-3 pl-5">
                {solution.dataPoints.map((point) => (
                  <li key={point} className="font-['Inter'] text-[14px] leading-[1.55] text-white/80 min-[1200px]:text-[15px]">
                    {point}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </section>
      ) : null}
    </>
  );
}
